<template>
  <div :class="['card-pricing bg-white border-0 mb-4', cardClass]">
    <div :class="['pt-4 pb-3 d-flex align-items-center card-header', headerClass]">
      <h4 class="text-uppercase ls-1 py-3 mb-0">{{ header }}</h4>
      <div v-if="isAdmin" class="py-3 ml-auto">
        <button type="button" :class="['btn btn-sm btn-secondary btn-icon', iconClass]" @click="openAddModal">
          <span class="btn-inner--icon"><i class="ni ni-fat-add"></i></span>
          <span class="font-weight-bold">Add</span>
        </button>
      </div>
    </div>
    <ul class="list-unstyled my-2">
      <li v-for="(feature, index) in features" :key="index" class="border-bottom py-2">
        <div class="row flex-grow-1 d-flex justify-content-between">
          <div class="col-5 pl-5">{{ toHeaderCase(feature.name) }}</div>
          <div class="col-4 border-left">{{ feature.value }}</div>
          <div v-if="isAdmin" class="col-3 border-left d-flex justify-content-center align-items-center">
            <base-button :class="buttonClass" size="sm" @click="openEditModal(index)">Edit</base-button>
          </div>
        </div>
      </li>
    </ul>
    <div v-if="features.length === 0" class="d-flex justify-content-center align-items-center text-muted h-100 py-9">
      <span>No Data Available</span>
    </div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" v-if="showModal">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ modalTitle }}</h5>
            <button type="button" class="close" @click="closeModal"><span>&times;</span></button>
          </div>
          <div class="modal-body">
            <div class="form-group" v-if="!isEditMode">
              <label for="featureName">Variable Name</label>
              <select class="form-control" v-model="modalData.name" id="variableName">
                <option disabled value="">Select a name</option>
                <option v-for="(name, index) in featureNames" :key="index" :value="name">
                  {{ toHeaderCase(name) }}
                </option>
              </select>
            </div>
            <div class="form-group" v-else>
              <label for="featureName">Variable Name</label>
              <input class="form-control" type="text" :value="toHeaderCase(modalData.name)" disabled />
            </div>
            <div class="form-group">
              <label for="featureValue">Value</label>
              <input type="text" class="form-control" v-model="modalData.value" id="variableValue"
                placeholder="Enter value" />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeModal">Close</button>
            <button class="btn btn-primary" @click="isEditMode ? updateFeature() : addFeature()">
              {{ isEditMode ? 'Save' : 'Add' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: "VariationCard",
  props: {
    cardClass: {
      type: String,
      default: ""
    },
    headerClass: {
      type: String,
      default: ""
    },
    buttonClass: {
      type: String,
      default: ""
    },
    header: {
      type: String,
      default: ""
    },
    features: {
      type: Array,
      required: true,
      default: () => []
    },
    iconClass: {
      type: String,
      default: ""
    },
  },
  data() {
    return {
      showModal: false,
      isEditMode: false,
      modalData: {
        name: '',
        value: ''
      },
      editingIndex: null,
      featureNames: [],
      isAdmin: false,
    };
  },
  computed: {
    modalTitle() {
      const action = this.isEditMode ? 'Edit' : 'Add';
      const cleanHeader = this.header?.replace(' variables', '') || '';
      return `${action} ${cleanHeader} Variable`;
    }
  },
  mounted() {
    this.isAdmin = sessionStorage.getItem("isAdmin");
  },
  methods: {
    toHeaderCase(str) {
      return str.toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    getTypeFromHeader(header) {
      const typeMap = {
        'Execution variables': 'EXECUTION',
        'Filter variables': 'FILTER',
        'Strategy variables': 'STRATEGY',
        'Stock variables': 'STOCK'
      };
      return typeMap[header?.trim()] || null;
    },
    openAddModal() {
      const type = this.getTypeFromHeader(this.header);
      if (type) {
        this.variationNameReference(type);
      }
      this.modalData = { name: '', value: '' };
      this.isEditMode = false;
      this.showModal = true;
    },
    openEditModal(index) {
      this.modalData = { ...this.features[index] };
      this.editingIndex = index;
      this.isEditMode = true;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    addFeature() {
      if (this.modalData.name && this.modalData.value) {
        const type = this.getTypeFromHeader(this.header);
        const variationId = sessionStorage.getItem('variationId');
        if (!variationId || !type) return;
        let data = {
          variationId: variationId,
          type: type,
          variableName: this.modalData.name,
          value: this.modalData.value
        };
        this.addVariables(data);
      }
    },
    updateFeature() {
      if (this.modalData.value) {
        const type = this.getTypeFromHeader(this.header);
        const variationId = sessionStorage.getItem('variationId');
        if (!variationId || !type) return;
        const data = {
          variationId: variationId,
          type: type,
          variableName: this.modalData.name,
          value: this.modalData.value
        };
        this.updateVariables(data);
      }
    },
    variationNameReference(type) {
      const data = { type };
      this.$store.dispatch("variationStore/getVariationNameReference", data, { root: true })
        .then(response => {
          this.featureNames = response?.server?.data || [];
        })
        .catch(error => {
          console.error("Failed to fetch feature names", error);
        });
    },
    addVariables(data) {
      this.$store.dispatch("variationStore/createNewVariables", data, { root: true })
        .then(() => {
          this.features.push({
            name: data.variableName,
            value: data.value
          });
          this.closeModal();
        })
        .catch(error => {
          console.error("Failed to fetch feature names", error);
        });
    },
    updateVariables(data) {
      this.$store.dispatch("variationStore/updateVariables", data, { root: true })
        .then(() => {
          if (this.editingIndex !== null && this.features[this.editingIndex]) {
            this.features[this.editingIndex].value = data.value;
          }
          this.closeModal();
        })
        .catch(error => {
          console.error("Failed to fetch feature names", error);
        });

    }
  }
};
</script>